import React, { useMemo } from 'react';

import { Alert, Box, Typography } from '@mui/material';

import TabPanelShell from '~/components/Offers/Detail/OfferBuilder/shared/TabPanelShell';

import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';

import {
  usePackageOptionsContext,
  usePricingToolSession,
  usePropertySetupContext,
  useScrapeSessionContext,
  useSurchargeGridsContext,
} from '../../PricingToolSessionContext';
import { usePricingToolTabCount } from '../../PricingToolTabCounts';
import SectionLabel from '../../shared/SectionLabel';
import { egsRooms, knownEgsRooms } from '../egs/egsModel';

import SurchargeDowTable from './SurchargeDowTable';
import SurchargePeriodsSection from './SurchargePeriodsSection';
import SurchargeSeasonalityTable from './SurchargeSeasonalityTable';
import { SEASON_GRIDS, WEEKDAYS, travelMonthsFrom } from './surchargeGridsModel';

interface Props {
  offerId?: string;
}

export default function SurchargesTab({ offerId }: Props) {
  const { session, setCost, setMonthSeason, setSell, addPeriod, updatePeriod, removePeriod } =
    useSurchargeGridsContext();
  const { parsedCsv, stayInputs } = useScrapeSessionContext();
  const { data: related } = useGetOpportunityRelated(offerId);
  const { roomAssets, optionState } = usePackageOptionsContext();
  const { draftIdBySfid } = usePricingToolSession();
  const { values: propertySetup, fx, currencyOptions, audRateFor } = usePropertySetupContext();
  const rooms = useMemo(() => egsRooms(optionState.roomsByCategory), [optionState.roomsByCategory]);
  const namedRooms = useMemo(
    () => knownEgsRooms(related, optionState.roomsByCategory, roomAssets, draftIdBySfid),
    [related, optionState.roomsByCategory, roomAssets, draftIdBySfid],
  );

  // fx.rate is already 1 for AUD.
  const currencyCode = fx.currency;
  const fxRateAudToNative = fx.rate ?? null;

  const months = useMemo(
    () =>
      travelMonthsFrom(
        (parsedCsv?.rows ?? []).map((row) => row.date),
        stayInputs?.nights,
      ),
    [parsedCsv, stayInputs?.nights],
  );

  const surchargedCells = useMemo(
    () =>
      SEASON_GRIDS.reduce(
        (total, season) => total + WEEKDAYS.filter((weekday) => session.grids[season.key][weekday] > 0).length,
        session.grids.periods.length,
      ),
    [session.grids],
  );
  usePricingToolTabCount('surcharges', surchargedCells || undefined);

  return (
    <TabPanelShell
      subtitle="Work out the nightly guest surcharge by day of week and season. Amounts feed the Scrape tab's parity figures, and still save to your session only - creating Surcharge Periods in Salesforce needs a backend endpoint that doesn't exist yet, so nothing here is submitted until that lands."
      title="Surcharges"
    >
      {!fx.isLoading && fxRateAudToNative == null && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Set the currency and FX rate on the Property Setup tab. Until then you can still type sell amounts, but the
          converted and margin figures stay blank.
        </Alert>
      )}

      <Box sx={{ mb: 4 }}>
        <SectionLabel>By day of week</SectionLabel>
        <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
          High and Shoulder amounts are added on top of All Year Round. The converted sell rounds down to whole units.
          Cost AUD is the typed cost divided by the exchange rate, or 80% of Sell AUD when no cost is typed. Margin is
          (Sell minus Cost) divided by Sell.
        </Typography>
        <SurchargeDowTable
          currencyCode={currencyCode}
          fxRateAudToNative={fxRateAudToNative}
          onCostChange={setCost}
          onSellChange={setSell}
          session={session}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionLabel>Dated surcharge periods</SectionLabel>
        <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
          A period adds its Sell AUD on top of the weekday grid, on every date in its range or only on the weekdays you
          pick. Use it for a surcharge the weekday grid cannot express, such as a public holiday or a named peak window.
        </Typography>
        <SurchargePeriodsSection
          audRateFor={audRateFor}
          currencyOptions={currencyOptions}
          defaultCurrency={propertySetup?.currency ?? currencyCode}
          fxRateAudToNative={fxRateAudToNative}
          grids={session.grids}
          onAdd={addPeriod}
          onRemove={removePeriod}
          onUpdate={updatePeriod}
          roomNames={namedRooms}
          rooms={rooms}
        />
      </Box>

      <Box>
        <SectionLabel>Assign seasonality to travel months</SectionLabel>
        <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
          Mark a month High or Shoulder to add that grid&apos;s weekday amounts on top of All Year Round. % Days
          Surcharged is the share of that month&apos;s days carrying a surcharge.
        </Typography>
        {months.length ? (
          <SurchargeSeasonalityTable grids={session.grids} months={months} onMonthSeasonChange={setMonthSeason} />
        ) : (
          <Typography color="text.secondary" variant="body2">
            Travel months appear once a rate shop has been loaded on the Scrape tab.
          </Typography>
        )}
      </Box>
    </TabPanelShell>
  );
}
